import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as xlsx from "xlsx";
import { useSnackbar } from "react-simple-snackbar";
import { makeStyles } from "@material-ui/core/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ClearIcon from "@material-ui/icons/Clear";
// import IconButton from "@material-ui/core/IconButton";
import {
  Button,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
  TablePagination,
  Stepper,
  Step,
  StepLabel,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Tooltip,
  Divider,
  IconButton,
} from "@material-ui/core";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import {
  createItems,
  getAllItems,
  updateItems,
  addStockForItems,
} from "../../actions/itemActions";
import {
  getAllCategories,
  createCategory,
} from "../../actions/categoryActions";
import { getAllUnits } from "../../actions/unitActions";
import { useHistory } from "react-router-dom";
import DescriptionIcon from "@material-ui/icons/Description";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    maxWidth: 1200,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(3),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    maxHeight: "50vh",
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
  },
  backButton: {
    borderRadius: 20,
  },
  button: {
    margin: theme.spacing(1),
    borderRadius: 20,
    padding: theme.spacing(1, 3),
    textTransform: "none",
  },
  input: {
    display: "none",
  },
  requiredField: {
    color: theme.palette.error.main,
    fontWeight: "bold",
  },
  invalidCount: {
    color: theme.palette.error.main,
    fontWeight: "bold",
  },
  stepper: {
    backgroundColor: "transparent",
    padding: theme.spacing(3, 0),
  },
  stepContent: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
  },
  card: {
    margin: theme.spacing(2, 0),
    boxShadow: theme.shadows[3],
  },
  clickableStep: {
    cursor: "pointer",
  },
  successMessage: {
    color: theme.palette.success.main,
    textAlign: "center",
    padding: theme.spacing(2),
  },
  statsGrid: {
    margin: theme.spacing(2, 0),
  },
  statCard: {
    padding: theme.spacing(2),
    textAlign: "center",
  },
}));

const BulkItemUpload = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { categories } = useSelector((state) => state.categories);
  const { units } = useSelector((state) => state.units);
  const [activeStep, setActiveStep] = useState(0);
  const [rawJson, setRawJson] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [openSnackbar] = useSnackbar();
  const [previewPage, setPreviewPage] = useState(0);
  const [previewRowsPerPage, setPreviewRowsPerPage] = useState(5);
  const [invalidPage, setInvalidPage] = useState(0);
  const [invalidRowsPerPage, setInvalidRowsPerPage] = useState(5);
  const [importResult, setImportResult] = useState(null);
  const { items } = useSelector((state) => state.items); // Added

  useEffect(() => {
    dispatch(getAllCategories());
    dispatch(getAllUnits());
    dispatch(getAllItems()); // Added
  }, [dispatch]);

  // Helper functions (unchanged)
  const hasRowErrors = (row) => {
    const basicChecks =
      !row.itemName?.trim() ||
      !row["Sale Unit"]?.trim() ||
      !row["Purchase Unit"]?.trim() || // Added
      row["Sale Price"] === undefined ||
      row["Sale Price"] === null ||
      row["Sale Price"] === "" ||
      row["Opening Stock"] === undefined ||
      row["Opening Stock"] === null ||
      row["Opening Stock"] === "" ||
      row["Opening Stock Purchase Cost"] === undefined || // Added
      row["Opening Stock Purchase Cost"] === null ||
      row["Opening Stock Purchase Cost"] === "";

    const salePrice = row["Sale Price"];
    const isSalePriceInvalid =
      (typeof salePrice === "string" && isNaN(Number(salePrice))) ||
      (typeof salePrice !== "number" &&
        !/^\d*\.?\d+$/.test(String(salePrice).trim()));

    const openingStock = row["Opening Stock"];
    const isOpeningStockInvalid =
      (typeof openingStock === "string" && isNaN(Number(openingStock))) ||
      (typeof openingStock !== "number" &&
        !/^\d*\.?\d+$/.test(String(openingStock).trim()));

    const openingStockPurchaseCost = row["Opening Stock Purchase Cost"];
    const isOpeningStockPurchaseCostInvalid =
      (typeof openingStockPurchaseCost === "string" &&
        isNaN(Number(openingStockPurchaseCost))) ||
      (typeof openingStockPurchaseCost !== "number" &&
        !/^\d*\.?\d+$/.test(String(openingStockPurchaseCost).trim()));

    return (
      basicChecks ||
      isSalePriceInvalid ||
      isOpeningStockInvalid ||
      isOpeningStockPurchaseCostInvalid
    );
  };

  const findCategoryId = (cName) => {
    const filteredCategory = categories.find(
      (c) => c.categoryName.toLowerCase() === cName.toLowerCase()
    );
    return filteredCategory ? filteredCategory.id : null;
  };

  const findUnitId = (uName) => {
    const normalizedUName = uName?.toLowerCase().trim();
    const filteredUnit = units.find(
      (unit) => unit.name.toLowerCase() === normalizedUName
    );
    return filteredUnit ? filteredUnit.id : null;
  };

  const createNewCategory = async (categoryName) => {
    let existingCategoryId = findCategoryId(categoryName);
    if (existingCategoryId) return existingCategoryId;

    const newCategory = { categoryName, isActive: true, isDeleted: false };
    try {
      const newCategoryData = await dispatch(createCategory(newCategory));
      return newCategoryData.id;
    } catch (error) {
      openSnackbar(
        `Failed to create category "${categoryName}": ${error.message}`
      );
      return null;
    }
  };

  const readUploadFile = (e) => {
    e.preventDefault();
    if (!e.target.files) return;

    const file = e.target.files[0];
    setSelectedFile(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target.result;
      const workbook = xlsx.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const json = xlsx.utils.sheet_to_json(worksheet, { defval: "" });
      setRawJson(json);
      setPreviewData(json);
      setPreviewPage(0);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImport = async () => {
    if (!rawJson || rawJson.length === 0) {
      openSnackbar("No valid file uploaded");
      return;
    }

    try {
      const categoryMap = new Map();
      const validRows = rawJson.filter((row) => !hasRowErrors(row));
      const uniqueCategoryNames = [
        ...new Set(
          validRows.map((item) => item["Category"] || "Uncategorized")
        ),
      ].filter((name) => name !== "Uncategorized");

      for (const categoryName of uniqueCategoryNames) {
        let categoryId = findCategoryId(categoryName);
        if (!categoryId) categoryId = await createNewCategory(categoryName);
        if (categoryId) categoryMap.set(categoryName, categoryId);
      }

      const newItems = [];
      const updateItemss = [];

      for (const item of rawJson) {
        if (hasRowErrors(item)) {
          openSnackbar(
            `Skipping item "${
              item.itemName || "unknown"
            }": Missing or invalid required fields`
          );
          continue;
        }

        const saleUnitId = findUnitId(item["Sale Unit"]) || units[0]?.id;
        const purchaseUnitId = findUnitId(item["Purchase Unit"]) || saleUnitId; // Added
        const categoryName = item["Category"] || "Uncategorized";
        const categoryId =
          categoryName === "Uncategorized"
            ? null
            : categoryMap.get(categoryName);

        const itemData = {
          itemName: item.itemName,
          units: {
            sale: {
              unit: saleUnitId,
              price: parseFloat(item["Sale Price"]) || 0,
            },
            purchase: {
              unit: purchaseUnitId,
              cost: parseFloat(item["Opening Stock Purchase Cost"]) || 0, // Updated
            },
            stock: {
              unit: item["Stock Unit"]
                ? findUnitId(item["Stock Unit"])
                : saleUnitId,
              quantity: parseFloat(item["Opening Stock"]) || 0,
            },
          },
          category: { categoryName, categoryId },
          toggleStock:
          String(item["Toggle Stock"] ?? "").trim().toLowerCase() === "yes"
            ? true
            : String(item["Toggle Stock"] ?? "").trim().toLowerCase() === "no"
            ? false
            : parseFloat(item["Opening Stock"]) > 0,
          barcode: item["Barcode"] || undefined,
          vat: parseFloat(item["VAT"]) || 0,
          stockQty: {
            initialQty: parseFloat(item["Opening Stock"]) || 0,
            currentQty: parseFloat(item["Opening Stock"]) || 0,
            minQty: parseFloat(item["Min Qty"]) || 0,
            maxQty: parseFloat(item["Max Qty"]) || 0,
          },
        };

        const existingItem = items.find(
          (i) => i.itemName.toLowerCase() === item.itemName.toLowerCase()
        );

        if (existingItem) {
          updateItemss.push({ ...itemData, id: existingItem.id });
        } else {
          newItems.push(itemData);
        }
      }

      if (newItems.length === 0 && updateItemss.length === 0) {
        openSnackbar("No valid items to import");
        return;
      }

      if (newItems.length > 0) {
        await dispatch(createItems(newItems, openSnackbar));
      }
      if (updateItemss.length > 0) {
        await dispatch(updateItems(updateItemss, openSnackbar));
        await dispatch(addStockForItems(updateItemss, openSnackbar));
      }

      setImportResult(newItems.length + updateItemss.length);
      dispatch(getAllItems());
      setRawJson(null);
      setPreviewData([]);
      setSelectedFile(null);
      setTimeout(() => {
        history.push("/ItemsList");
      }, 2000);
    } catch (error) {
      openSnackbar("Failed to import items: " + error.message);
    }
  };

  const downloadSampleFile = () => {
    const headers = [
      "itemName",
      "Sale Unit",
      "Sale Price",
      "Opening Stock",
      "Purchase Unit", // Added
      "Opening Stock Purchase Cost", // Added
      "Category",
      "Stock Unit",
      "Toggle Stock",
      "Min Qty",
      "Max Qty",
    ];
    const sampleData = [
      headers,
      [
        "Aashirwad Atta 2kg",
        "Kilogram",
        120,
        50,
        "Kilogram", // Added
        100, // Added
        "ATTA",
        "Kilogram",
        "Yes",
        10,
        100,
      ],
      [
        "Sugar",
        "Kilogram",
        40,
        10,
        "Kilogram", // Added
        35, // Added
        "Groceries",
        "Kilogram",
        "No",
        5,
        100,
      ],
    ];
    const ws = xlsx.utils.aoa_to_sheet(sampleData);
    const wb = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(wb, ws, "Sample");
    xlsx.writeFile(wb, "sample_items.xlsx");
  };

  const clearFile = () => {
    setSelectedFile(null);
    setRawJson(null);
    setPreviewData([]);
    setPreviewPage(0);
    setInvalidPage(0);
    document.getElementById("upload-file").value = null;
  };

  const handleStepClick = (index) => {
    if (index <= activeStep) {
      setActiveStep(index);
    } else if (index === 1 && rawJson && rawJson.length > 0) {
      setActiveStep(1);
    } else if (index === 2 && rawJson && rawJson.length > 0) {
      setActiveStep(2);
    } else {
      openSnackbar("Please complete the previous steps first");
    }
  };

  const handlePreviewPageChange = (event, newPage) => {
    setPreviewPage(newPage);
  };

  const handlePreviewRowsPerPageChange = (event) => {
    setPreviewRowsPerPage(parseInt(event.target.value, 10));
    setPreviewPage(0);
  };

  const handleInvalidPageChange = (event, newPage) => {
    setInvalidPage(newPage);
  };

  const handleInvalidRowsPerPageChange = (event) => {
    setInvalidRowsPerPage(parseInt(event.target.value, 10));
    setInvalidPage(0);
  };

  const steps = ["Upload File & Preview", "Analyze Data", "Import Results"];

  const handleNext = () => {
    if (activeStep === 0 && (!rawJson || rawJson.length === 0)) {
      openSnackbar("Please upload a file first");
      return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    if (activeStep === 0) {
      history.push("/ItemsList");
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box className={classes.stepContent}>
            <Box display="flex" justifyContent="center" mb={2}>
              <input
                accept=".xlsx, .xls, .csv"
                className={classes.input}
                id="upload-file"
                type="file"
                onChange={readUploadFile}
                disabled={!!selectedFile}
              />
              <label htmlFor="upload-file">
                <Button
                  variant="contained"
                  color="primary"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  className={classes.button}
                  disabled={!!selectedFile}
                >
                  Upload File
                </Button>
              </label>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<CloudDownloadIcon />}
                onClick={downloadSampleFile}
                className={classes.button}
              >
                Download Sample
              </Button>
            </Box>
            {/* {selectedFile && (
              <Typography variant="body1" align="center" gutterBottom>
                <span>Selected File: {selectedFile.name}</span>
                <IconButton onClick={clearFile} aria-label="Clear" size="small">
                  <ClearIcon />
                </IconButton>
              </Typography>
            )} */}

            {selectedFile && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                bgcolor="grey.100"
                borderRadius="8px"
                p={1}
                my={2}
                maxWidth="500px"
                mx="auto"
                // boxShadow={1}
              >
                <Typography
                  variant="body2"
                  color="textSecondary"
                  align="center"
                  paragraph
                >
                  <span
                    style={{
                      fontWeight: "bold",
                      color: "#000",
                      marginRight: "10px",
                    }}
                  >
                    Selected File:
                  </span>
                  <Chip
                    icon={<DescriptionIcon />}
                    label={selectedFile.name}
                    variant="outlined"
                    color="primary"
                    deleteIcon={
                      <Tooltip title="Clear File">
                        <IconButton
                          size="small"
                          aria-label="Clear selected file"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    }
                    onDelete={clearFile}
                    sx={{
                      maxWidth: "90%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: "0.9rem",
                      padding: "8px",
                    }}
                  />
                </Typography>
              </Box>
            )}

            <Typography
              variant="body2"
              color="textSecondary"
              align="center"
              paragraph
            >
              <span style={{ fontWeight: "bold", color: "#000" }}>
                Required fields
                <span style={{ color: "red" }}>*</span>
              </span>
              : <span className={classes.requiredField}>Item Name</span>,{" "}
              <span className={classes.requiredField}>Sale Unit</span>,{" "}
              <span className={classes.requiredField}>Sale Price (number)</span>
              ,{" "}
              <span className={classes.requiredField}>
                Opening Stock (number)
              </span>
              , <span className={classes.requiredField}>Purchase Unit</span>,{" "}
              <span className={classes.requiredField}>
                Opening Stock Purchase Cost (number)
              </span>
              <br />
              <span style={{ color: "red" }}>
                Rows in red indicate invalid data and will be skipped
              </span>
            </Typography>

            {previewData.length > 0 && (
              <TableContainer
                component={Paper}
                className={classes.tableContainer}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>S.No</TableCell>
                      {Object.keys(previewData[0]).map((key) => (
                        <TableCell key={key}>{key}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData
                      .slice(
                        previewPage * previewRowsPerPage,
                        previewPage * previewRowsPerPage + previewRowsPerPage
                      )

                      .map((row, index) => (
                        <TableRow
                          key={index}
                          style={
                            hasRowErrors(row)
                              ? { backgroundColor: "#ed4a4a" }
                              : {}
                          }
                        >
                          <TableCell>
                            <TableCell>
                              {previewPage * previewRowsPerPage + index + 1}
                            </TableCell>
                          </TableCell>
                          {Object.keys(previewData[0]).map((key, i) => (
                            <TableCell key={i}>
                              {row[key] === undefined ||
                              row[key] === null ||
                              row[key] === ""
                                ? "-"
                                : row[key]}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={previewData.length}
                  rowsPerPage={previewRowsPerPage}
                  page={previewPage}
                  onPageChange={handlePreviewPageChange}
                  onRowsPerPageChange={handlePreviewRowsPerPageChange}
                />
              </TableContainer>
            )}
          </Box>
        );
      case 1:
        const invalidRows = previewData.filter(hasRowErrors);
        return (
          <Box className={classes.stepContent}>
            <Grid container spacing={2} className={classes.statsGrid}>
              <Grid item xs={4}>
                <Card className={classes.statCard}>
                  <Typography variant="h6">Total Items</Typography>
                  <Typography variant="h4">{previewData.length}</Typography>
                </Card>
              </Grid>
              <Grid item xs={4}>
                <Card className={classes.statCard}>
                  <Typography variant="h6">Valid Items</Typography>
                  <Typography variant="h4" color="primary">
                    {previewData.length - invalidRows.length}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={4}>
                <Card className={classes.statCard}>
                  <Typography variant="h6">Invalid Items</Typography>
                  <Typography variant="h4" className={classes.invalidCount}>
                    {invalidRows.length}
                  </Typography>
                </Card>
              </Grid>
            </Grid>
            {invalidRows.length > 0 && (
              <Card className={classes.card}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Invalid Rows Details
                  </Typography>
                  <TableContainer className={classes.tableContainer}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>S.No</TableCell>
                          {Object.keys(previewData[0]).map((key) => (
                            <TableCell key={key}>{key}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invalidRows
                          .slice(
                            invalidPage * invalidRowsPerPage,
                            invalidPage * invalidRowsPerPage +
                              invalidRowsPerPage
                          )
                          .map((row, index) => (
                            <TableRow
                              key={index}
                              style={{ backgroundColor: "#ed4a4a" }}
                            >
                              <TableCell>
                                <TableCell>
                                  {invalidPage * invalidRowsPerPage + index + 1}
                                </TableCell>
                              </TableCell>
                              {Object.keys(previewData[0]).map((key, i) => (
                                <TableCell key={i}>
                                  {row[key] === undefined ||
                                  row[key] === null ||
                                  row[key] === ""
                                    ? "-"
                                    : row[key]}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      component="div"
                      count={invalidRows.length}
                      rowsPerPage={invalidRowsPerPage}
                      page={invalidPage}
                      onPageChange={handleInvalidPageChange}
                      onRowsPerPageChange={handleInvalidRowsPerPageChange}
                    />
                  </TableContainer>
                </CardContent>
              </Card>
            )}
          </Box>
        );
      case 2:
        return (
          <Box className={classes.stepContent}>
            {importResult ? (
              <Card className={classes.card}>
                <CardContent>
                  <Typography variant="h5" className={classes.successMessage}>
                    Successfully imported {importResult} valid items!
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Box display="flex" justifyContent="center">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleImport}
                  className={classes.button}
                  size="large"
                >
                  Import Items
                </Button>
              </Box>
            )}
          </Box>
        );
      default:
        return "Unknown step";
    }
  };

  return (
    <Container className={classes.container}>
      <Box className={classes.header}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          className={classes.backButton}
        >
          Back to ItemList
        </Button>
        <Typography variant="h4">Bulk Item Upload</Typography>
        <div style={{ width: 100 }} /> {/* Spacer */}
      </Box>
      <Stepper activeStep={activeStep} className={classes.stepper}>
        {steps.map((label, index) => (
          <Step
            key={label}
            onClick={() => handleStepClick(index)}
            className={classes.clickableStep}
          >
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <Box>
        {getStepContent(activeStep)}
        <Box display="flex" justifyContent="flex-end" mt={3}>
          {activeStep !== 0 && (
            <Button
              variant="outlined"
              color="primary"
              onClick={handleBack}
              className={classes.button}
            >
              Previous
            </Button>
          )}
          {activeStep !== steps.length - 1 && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              className={classes.button}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default BulkItemUpload;
